import { fromWire } from '@/api/wire';
import type { StreamEvent, StreamEventKind } from '@/api/types';

/**
 * The stream's frame vocabulary (ADR 0002).
 *
 * ## Why this list exists as a list
 *
 * Every frame the server writes is **named** — `event: new_mail`, `event:
 * flags`, … — and `EventSource` delivers a named frame **only** to a listener
 * registered for that exact name. `onmessage` sees unnamed frames and nothing
 * else, so a client that only implements `onmessage` connects successfully,
 * receives its heartbeats, reports itself healthy, and never learns that a
 * single thing changed. That failure is silent and indistinguishable from "no
 * mail arrived", which is why the names are enumerated here, subscribed in a
 * loop, and asserted by a test.
 *
 * The heartbeat is an SSE *comment* (`: hb`), not a frame: it keeps
 * intermediaries from closing the stream and is never delivered to JavaScript.
 */
export const STREAM_EVENT_NAMES: readonly StreamEventKind[] = [
  'new_mail',
  'flags',
  'expunge',
  'folder_counts',
  'folder_invalidated',
  'folder_list',
  'account_status',
  'resync_required',
  'tasks_changed',
  'labels_changed',
  'hello',
  'goodbye',
];

const NAME_SET = new Set<string>(STREAM_EVENT_NAMES);

export function isStreamEventKind(name: string): name is StreamEventKind {
  return NAME_SET.has(name);
}

/**
 * Parses one frame's `data` into a `StreamEvent`.
 *
 * The event *name* is the authority on the kind, not the `kind` member of the
 * payload: the name is what `EventSource` dispatched on, and trusting a
 * mismatched body member would let one frame be handled as another. The payload
 * goes through the same `fromWire` conversion every REST response does, so
 * `folder_id` arrives here as `folderId` and no second mapper exists to drift.
 *
 * Returns `undefined` for anything unparseable rather than throwing: one bad
 * frame must not tear down a stream that is otherwise delivering.
 */
export function parseStreamEvent(kind: StreamEventKind, data: string): StreamEvent | undefined {
  let payload: unknown;
  try {
    payload = data === '' ? {} : JSON.parse(data);
  } catch {
    return undefined;
  }
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return undefined;
  const converted = fromWire<Record<string, unknown>>(payload);
  return { ...(converted as Partial<StreamEvent>), kind };
}

/**
 * The `message_ids` a frame names, for one folder.
 *
 * Ids are **opaque**. This reads `messageIds` as handed over and never builds
 * one: `uids` and `uidValidity` are present on the frame for correlation and
 * for the invalidation check, and recombining them into an id would duplicate
 * an encoding that belongs to the backend (`EncodeMessageID`) and would break
 * the first time a mailbox name contained the separator.
 */
export function messageIdsOf(event: StreamEvent): string[] {
  return event.messageIds ?? [];
}

/**
 * The message id positionally paired with `uid`, or `undefined`.
 *
 * `flags` frames carry their changes keyed by `uid`, while the ids are in a
 * parallel `messageIds` array minted from `uids` in order. Pairing by index is
 * therefore *reading* the correlation the server already made — it is not
 * decoding an id, and nothing here depends on what a uid or an id contains.
 */
export function messageIdForUid(event: StreamEvent, uid: number): string | undefined {
  const index = event.uids?.indexOf(uid) ?? -1;
  if (index < 0) return undefined;
  return event.messageIds?.[index];
}
